import { Box, Snackbar, TextField } from '@mui/material';
import { useIsMutating } from '@tanstack/react-query';
import _ from 'lodash';
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import AccordionSection from '../components/AccordionSection';
import DuplicateModal from '../components/DuplicateModal';
import ItemHeader from '../components/common/ItemHeader/ItemHeader';
import MoreOptions from '../components/common/MoreOptions/MoreOptions';
import TagsSection from '../components/common/TagsSection/TagsSection';
import TemplateFieldsSection from '../components/common/TemplateFieldsSection/TemplateFieldsSection';
import PreferencePicker from '../components/preferences/picker/PreferencePicker';
import { DisableContext } from '../contexts/DisableContextProvider';
import { ROUTE_PATHS } from '../routes/Routes';
import { useCreateContactTemplate, useGetContactTemplateById, useUpdateContactTemplate } from '../services/react-query/contact_templates';
import { mutationKeys } from '../services/react-query/mutationKeys';
import { useGetAllPreferences } from '../services/react-query/preferences';
import { inputContainerStyling, inputDividerStyling } from '../styles/sxStyling';
import { TemplateField } from '../types/FieldCommon';
import { PREFERENCES_VALID_KEYS, Preference } from '../types/Preference';
import { UserSettingsCollapseKeys } from '../types/User';
import { Loading } from '../uikit';
import { errorPopulator, uuidFinder } from '../utility/inputUtils';
import { ContactTemplate } from '../types/ContactTemplate';
import { useArchiveContactTemplate } from '../services/react-query/contact_templates/useArchiveContactTemplate';
import { useDuplicateContactTemplate } from '../services/react-query/contact_templates/useDuplicateContactTemplate';

export default function ContactTemplateItem() {
  const { uuid } = useParams();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<ContactTemplate | null>(null);
  const [saved, setSaved] = useState(false);
  const [isDuplicateModalOpen, setIsDuplicateModalOpen] = useState(false);

  const isNew = uuid === 'new';

  const { data, isLoading } = useGetContactTemplateById({ uuid });

  const { mutate: createTemplate, isPending: isCreating } = useCreateContactTemplate({
    onSuccessCallback: () => null,
  });

  const { mutate: updateTemplate, isPending: isUpdating } = useUpdateContactTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => setSaved(true),
  });

  const { mutate: archiveTemplate } = useArchiveContactTemplate({
    uuid: _.toString(uuid),
    onSuccessCallback: () => navigate(`/${ROUTE_PATHS.CONTACT_TEMPLATES}`),
  });

  const { mutate: duplicateTemplateMutation } = useDuplicateContactTemplate((newUuid) => {
    navigate(`/${ROUTE_PATHS.CONTACT_TEMPLATES}/${newUuid}`);
    setIsDuplicateModalOpen(false);
  });

  useEffect(() => {
    if (data) setTemplate(data);
  }, [data]);

  const { disabled, selfDisable } = useContext(DisableContext);
  selfDisable('archived', !!template?.discarded_at);

  const { data: preferencesData, isFetching } = useGetAllPreferences({
    params: {
      filters: {
        keys: [PREFERENCES_VALID_KEYS.CONTACT_TITLE, PREFERENCES_VALID_KEYS.CONTACT_ROLE],
      },
    },
  });

  const preferences = preferencesData ?? [];

  const titleOptions = useMemo(
    () =>
      _.filter(preferences, {
        key: PREFERENCES_VALID_KEYS.CONTACT_TITLE,
      }),
    [preferences]
  );

  const roleOptions = useMemo(
    () =>
      _.filter(preferences, {
        key: PREFERENCES_VALID_KEYS.CONTACT_ROLE,
      }),
    [preferences]
  );

  const showLoading = !template || isLoading;
  if (showLoading) return <Loading />;

  const handleSave = () => {
    if (!template) return;

    if (isNew) createTemplate(template);
    else updateTemplate(template);
  };

  const setFields = (fields: TemplateField[]) => setTemplate({ ...template, fields });

  const handleDuplicate = (name: string) => {
    duplicateTemplateMutation({ uuid: _.toString(uuid), name });
  };

  return (
    <Box>
      <ItemHeader
        title={`${isNew ? 'Create' : 'Update'} Contact Template`}
        onSave={handleSave}
        archived={!!template.discarded_at}
        disableActions={isCreating || isUpdating}
        showMoreOptions={
          !isNew ? (
            <MoreOptions onArchive={archiveTemplate} onDuplicate={() => setIsDuplicateModalOpen(true)} name={template?.name} itemName="contact template" />
          ) : null
        }
      />

      <AccordionSection title="Basic Information" collapseId={UserSettingsCollapseKeys.CONTACT_TEMPLATES_ITEM_INFORMATION}>
        <Box sx={inputContainerStyling}>
          {/* LEFT COLUMN */}
          <Box sx={inputDividerStyling}>
            <TextField
              label="Name"
              value={template?.name ?? ''}
              autoFocus
              onChange={(e) =>
                setTemplate({
                  ...template,
                  name: e.target.value,
                })
              }
              disabled={disabled}
              {...errorPopulator(template, 'name')}
            />

            <PreferencePicker
              label="Title"
              options={titleOptions}
              loading={isFetching}
              value={uuidFinder(titleOptions, template?.contact_title_uuid) ?? null}
              onValueChange={(v) => {
                const pref = v as Preference | null;

                setTemplate((prev) => (prev ? { ...prev, contact_title_uuid: pref?.uuid } : prev));
              }}
              disabled={disabled}
              helperText={errorPopulator(template, 'title')?.helperText}
            />
          </Box>

          {/* RIGHT COLUMN */}
          <Box sx={inputDividerStyling}>
            <PreferencePicker
              label="Roles"
              options={roleOptions}
              multiple
              loading={isFetching}
              value={_.filter(roleOptions, (p) => _.includes(template.roles_uuids, p.uuid))}
              onValueChange={(vals) => setTemplate((prev) => (prev ? { ...prev, roles_uuids: (vals as Preference[]).map((p) => p.uuid) } : prev))}
              disabled={disabled}
              helperText={errorPopulator(template, 'roles')?.helperText}
            />
          </Box>
        </Box>
      </AccordionSection>

      <TemplateFieldsSection fields={template.fields} setFields={setFields} collapseId={UserSettingsCollapseKeys.CONTACT_TEMPLATES_ITEM_FIELDS} />

      <TagsSection
        tags={template.tags}
        collapseId={UserSettingsCollapseKeys.CONTACT_TEMPLATES_ITEM_TAGS}
        setTags={(tags) =>
          setTemplate({
            ...template,
            tags,
          })
        }
      />

      <DuplicateModal
        isOpen={isDuplicateModalOpen}
        onCancel={() => setIsDuplicateModalOpen(false)}
        onConfirm={(name) => handleDuplicate(name)}
        defaultName={`${template?.name ?? ''} Copy`}
        itemName="contact template"
      />

      <Snackbar open={saved} onClose={() => setSaved(false)} message="Saved!" />
    </Box>
  );
}
